import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { formatCount } from '@/i18n/format';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appDataCopy from '@/i18n/ns/app-data';
import readyCopy from '@/i18n/ns/ready';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import type { DataColumn, DataRow, RowsState } from './data-model';
import {
  firstEntryName,
  latestTime,
  orderEntries,
  parseJson,
  pickDisplay,
  rawRow,
  tagValues,
} from './readable-model';
import './data-readable.css';

type Ready = Extract<RowsState, { status: 'ready' }>;
const PAGE = 20;
const LONG = 240;

/** One value, as a person reads it: structure first, never JSON text by default. */
function Value({
  column,
  raw,
  format,
  absent,
  more,
  items,
  moreColumns,
}: {
  column: DataColumn;
  raw: unknown;
  format: (value: string, type: string) => string;
  absent: string;
  more: string;
  items: (n: number) => string;
  moreColumns: (n: number) => string;
}) {
  const [open, setOpen] = useState(false);
  if (raw === null || raw === undefined || raw === '')
    return <span className="pc-readable-muted">{absent}</span>;
  if (column.type === 'json') {
    const parsed = parseJson(raw);
    return (
      <Nested
        value={parsed === undefined ? String(raw) : parsed}
        items={items}
        moreColumns={moreColumns}
      />
    );
  }
  if (typeof raw === 'string' && (column.type === 'timestamp' || column.type === 'date'))
    return <>{format(raw, column.type)}</>;
  if (typeof raw === 'string' && raw.length > LONG)
    return (
      <>
        <span className="pc-readable-clamp" data-open={open || undefined}>
          {raw}
        </span>
        <button type="button" className="pc-readable-link" onClick={() => setOpen((v) => !v)}>
          {more}
        </button>
      </>
    );
  return <>{String(raw)}</>;
}

/** A parsed JSON value: arrays of objects as small tables (five columns, then "+n more"), objects as key/values. */
function Nested({
  value,
  items,
  moreColumns,
  depth = 0,
}: {
  value: unknown;
  items: (n: number) => string;
  moreColumns: (n: number) => string;
  depth?: number;
}) {
  if (value === null || value === undefined) return <span>—</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="pc-readable-muted">{items(0)}</span>;
    const objects = value.filter(
      (v): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v),
    );
    if (objects.length === value.length && depth < 2) {
      const keys = [...new Set(objects.flatMap((o) => Object.keys(o)))];
      const shown = keys.slice(0, 5);
      return (
        <table className="pc-readable-mini" data-testid="data-nested-table">
          <thead>
            <tr>
              {shown.map((key) => (
                <th key={key} scope="col">
                  {key}
                </th>
              ))}
              {keys.length > shown.length && <th>{moreColumns(keys.length - shown.length)}</th>}
            </tr>
          </thead>
          <tbody>
            {objects.map((object, index) => (
              <tr key={index}>
                {shown.map((key) => (
                  <td key={key}>
                    <Nested
                      value={object[key]}
                      items={items}
                      moreColumns={moreColumns}
                      depth={depth + 1}
                    />
                  </td>
                ))}
                {keys.length > shown.length && <td />}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return (
      <>{value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ')}</>
    );
  }
  if (typeof value === 'object') {
    return (
      <dl className="pc-readable-kv pc-readable-nested">
        {Object.entries(value as Record<string, unknown>).map(([key, inner]) => (
          <div key={key}>
            <dt>{key}</dt>
            <dd>
              <Nested value={inner} items={items} moreColumns={moreColumns} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <>{String(value)}</>;
}

function Entry({
  columns,
  row,
  line,
  expanded,
  onToggle,
  onRowHistory,
  format,
}: {
  columns: readonly DataColumn[];
  row: DataRow;
  line: string;
  expanded: boolean;
  onToggle: () => void;
  onRowHistory?: (rowId: string) => void;
  format: (value: string, type: string) => string;
}) {
  const ready = useNamespaceCopy(readyCopy);
  const copy = useNamespaceCopy(appDataCopy);
  const locale = useChatInterfaceLocale();
  const [raw, setRaw] = useState(false);
  const items = (n: number) => formatCount(ready.dataItemsNoun, n, locale);
  const moreColumns = (n: number) => formatCopy(ready.dataMoreColumns, { n: String(n) });
  const json = useMemo(() => JSON.stringify(rawRow(columns, row), null, 2), [columns, row]);
  return (
    <li
      className="pc-readable-entry"
      data-testid="data-entry"
      data-expanded={expanded || undefined}
    >
      <button
        type="button"
        className="pc-readable-toggle"
        data-testid="data-entry-toggle"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="pc-readable-line">{line || copy.absent}</span>
      </button>
      {expanded && (
        <div className="pc-readable-detail">
          <dl className="pc-readable-kv">
            {columns
              .filter((column) => column.type !== 'file' && !column.image)
              .map((column) => {
                const parsed =
                  column.type === 'json' ? parseJson(row.cells[column.key]) : undefined;
                return (
                  <div key={column.key}>
                    <dt>{column.label}</dt>
                    <dd>
                      <Value
                        column={column}
                        raw={parsed !== undefined ? row.cells[column.key] : row.cells[column.key]}
                        format={format}
                        absent={copy.absent}
                        more={ready.dataShowMore}
                        items={items}
                        moreColumns={moreColumns}
                      />
                    </dd>
                  </div>
                );
              })}
          </dl>
          <div className="pc-readable-tools">
            <button
              type="button"
              className="pc-readable-link"
              data-testid="data-show-raw"
              aria-expanded={raw}
              onClick={() => setRaw((v) => !v)}
            >
              {ready.dataShowRaw}
            </button>
            {onRowHistory && (
              <button
                type="button"
                className="pc-readable-link"
                data-testid="row-history-open"
                onClick={() => onRowHistory(row.id)}
              >
                {copy.rowHistory}
              </button>
            )}
          </div>
          {raw && (
            <div className="pc-readable-raw" data-testid="data-raw">
              <pre>{json}</pre>
              <button
                type="button"
                className="pc-readable-link"
                data-testid="data-copy"
                onClick={() => void navigator.clipboard?.writeText(json)}
              >
                {ready.dataCopy}
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/** A saved table as a summary and a readable list; the structured entry and the raw JSON are one click and two clicks away. */
export function ReadableRows({
  state,
  onRowHistory,
  onMore,
}: {
  state: Ready;
  onRowHistory?: (rowId: string) => void;
  onMore: () => void;
}) {
  const ready = useNamespaceCopy(readyCopy);
  const copy = useNamespaceCopy(appDataCopy);
  const locale = useChatInterfaceLocale();
  const [shown, setShown] = useState(PAGE);
  const [open, setOpen] = useState<string | null>(null);
  const display = useMemo(
    () => pickDisplay(state.columns, state.rows),
    [state.columns, state.rows],
  );
  const ordered = useMemo(() => orderEntries(state.rows, display.time), [state.rows, display.time]);
  const latest = latestTime(state.rows, display.time);
  const format = (value: string, type: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(
      locale,
      type === 'date' ? { dateStyle: 'medium' } : { dateStyle: 'medium', timeStyle: 'short' },
    ).format(date);
  };
  const tags = tagValues(state.rows, display.tag);
  const list = new Intl.ListFormat(locale, { type: 'conjunction' });
  const count = state.rows.length;
  const summary = [
    latest
      ? formatCopy(ready.dataSummary, { count: String(count), time: format(latest, 'timestamp') })
      : formatCount(ready.dataItemsNoun, count, locale),
    tags.length > 0 && tags.length <= 4 ? list.format(tags) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const lineOf = (row: DataRow) => {
    const parts: string[] = [];
    const cell = (key: string | null) => (key ? row.cells[key] : null);
    const time = cell(display.time);
    if (typeof time === 'string' && time) parts.push(format(time, 'timestamp'));
    const tag = cell(display.tag);
    if (typeof tag === 'string' && tag) parts.push(tag);
    if (display.count) {
      const parsed = parseJson(cell(display.count));
      if (Array.isArray(parsed)) {
        parts.push(formatCount(ready.dataItemsNoun, parsed.length, locale));
        const top = firstEntryName(parsed);
        if (top) parts.push(top);
      }
    }
    const other = cell(display.other);
    if (typeof other === 'string' && other) parts.push(other);
    return parts.join(' · ');
  };
  return (
    <div className="pc-readable" data-testid="data-readable">
      <p className="pc-readable-summary" data-testid="data-summary">
        {summary}
      </p>
      <p className="pc-readable-auto" data-testid="data-auto-fields">
        {ready.dataAutoFields}
      </p>
      <ul className="pc-readable-list">
        {ordered.slice(0, shown).map((row) => (
          <Entry
            key={row.id}
            columns={state.columns}
            row={row}
            line={lineOf(row)}
            expanded={open === row.id}
            onToggle={() => setOpen((current) => (current === row.id ? null : row.id))}
            onRowHistory={onRowHistory}
            format={format}
          />
        ))}
      </ul>
      {shown < ordered.length && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="data-show-more"
          onClick={() => setShown((n) => n + PAGE)}
        >
          {ready.dataShowMore}
        </Button>
      )}
      {state.hasMore && shown >= ordered.length && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="rows-more"
          onClick={() => onMore()}
        >
          {copy.loadMore}
        </Button>
      )}
    </div>
  );
}
